import { useState } from "react";
import Search from "../../../form/Search";
import Table from "../../../elements/table/Table";
import Button from "../../../form/Button";
import { useOrganization } from "../../../../context/OrganizationContext";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { useDebounce } from "../../../../hooks/useDebounce";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ticketDelete, ticketList } from "../../../../services/ticketService";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { lngDetect } from "../../../../helpers/lngDetect";
import dayjs from "dayjs";
import DeleteModal from "../../../common/DeleteModal";
import { classNames } from "../../../../helpers/classNames";
import Badge from "../../../elements/Badge";
import { t } from "i18next";

// Enhanced styling function for ticket types
const getTypeStyle = (type) => {
  const typeLower = type?.toLowerCase();
  const typeStyles = {
    "addons-ticket": {
      backgroundColor: "#DBEAFE",
      color: "#1D4ED8",
      borderColor: "#3B82F6",
    },
    "addons-event": {
      backgroundColor: "#FCE7F3",
      color: "#BE185D",
      borderColor: "#EC4899",
    },
    ticket: {
      backgroundColor: "#D1FAE5",
      color: "#047857",
      borderColor: "#10B981",
    },
    variation: {
      backgroundColor: "#FEF3C7",
      color: "#D97706",
      borderColor: "#F59E0B",
    },
  };

  return (
    typeStyles[typeLower] || {
      backgroundColor: "#F3F4F6",
      color: "#374151",
      borderColor: "#6B7280",
    }
  );
};

// Format type text for display
const formatTypeText = (type) => {
  const typeMap = {
    "addons-ticket": "Addon Ticket",
    "addons-event": "Addon Event",
    ticket: "Ticket",
    variation: "Variation",
  };

  return (
    typeMap[type?.toLowerCase()] ||
    type?.charAt(0)?.toUpperCase() + type?.slice(1)
  );
};

const TicketsList = () => {
  const queryClient = useQueryClient();
  const { organizations, selectedOrganization } = useOrganization();
  const { lang } = useSelector((state) => state?.langProvider);
  const navigate = useNavigate();
  const { eventId } = useParams();
  const { event, eventLoading, selectedLanguage } = useOutletContext();
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
    organization: selectedOrganization,
  });

  const {
    data: ticket,
    isLoading: ticketLoading,
    isFetching: ticketFetching,
  } = useQuery({
    queryKey: ["ticket", filters, search, eventId],
    queryFn: async () => {
      const res = await ticketList({
        ...filters,
        eventUid: eventId,
        ...(search && { keyword: search }),
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
    enabled: !!eventId,
  });

  // Ticket type change handler
  // const { mutate: typeChangeHandler, isPending: typeChangeInPending } =
  //   useMutation({
  //     mutationFn: async ({ id, type }) => {
  //       queryClient.setQueryData(
  //         ["ticket", filters, search, eventId],
  //         (state) => {
  //           if (state) {
  //             let stateData = JSON.parse(JSON.stringify(state?.data));
  //             stateData = (stateData || []).map((elm) =>
  //               elm?._id === id
  //                 ? {
  //                     ...elm,
  //                     type: type,
  //                   }
  //                 : elm
  //             );
  //             return { ...state, data: stateData };
  //           }
  //         }
  //       );
  //       const res = await ticketTypeChange({
  //         ids: [id],
  //         type: type,
  //       });
  //       return { res, payload: { id, type } };
  //     },
  //     onSuccess: ({ res, payload }) => {
  //       if (res?.status === 200) {
  //         toast.success("Type updated successfully!");
  //       } else {
  //         queryClient.setQueryData(
  //           ["ticket", filters, search, eventId],
  //           (state) => {
  //             if (state) {
  //               let stateData = JSON.parse(JSON.stringify(state?.data));
  //               stateData = (stateData || []).map((elm) =>
  //                 elm?._id === payload?.id
  //                   ? {
  //                       ...elm,
  //                       type: payload?.type,
  //                     }
  //                   : elm
  //               );
  //               return { ...state, data: stateData };
  //             }
  //           }
  //         );
  //         toast.warning(res?.message || "Failed to update type");
  //       }
  //     },
  //     onError: (error) => {
  //       toast.error("Failed to update type");
  //       console.log(error);
  //     },
  //   });

  // Type cycle function
  const getNextType = (currentType) => {
    const typeOrder = ["addons-ticket", "addons-event", "ticket", "variation"];
    const currentIndex = typeOrder.indexOf(currentType?.toLowerCase());
    const nextIndex = (currentIndex + 1) % typeOrder.length;
    return typeOrder[nextIndex];
  };

  const tableHeadData = [
    {
      _id: "1",
      name: t("Name"),
      type: "text",
      align: "left",
    },
    {
      _id: "2",
      name: t("Available quantity"),
      type: "text",
      align: "center",
    },
    {
      _id: "0",
      name: t("Booking/Full paid"),
      type: "text",
      align: "center",
    },
    {
      _id: "3",
      name: t("Price"),
      type: "text",
      align: "center",
    },
    {
      _id: "4",
      name: t("Sales time start"),
      type: "text",
      align: "center",
    },
    {
      _id: "5",
      name: t("Sales time end"),
      type: "text",
      align: "center",
    },
    {
      _id: "type",
      name: t("Type"),
      type: "text",
      align: "center",
    },
    {
      _id: "6",
      name: t("Action"),
      type: "action",
      align: "right",
    },
  ];

  const tableData = ticket?.data?.map((item) => {
    const typeStyle = getTypeStyle(item?.type);

    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "user",
          align: "left",
          cellClass: classNames(
            "!min-w-[100px]",
            item?.type !== "addons-event"
              ? "[&>div>div>img]:p-1"
              : "[&>div>div>img]:object-contain"
          ),
          data: lngDetect(item?.name) || "-",
          image:
            item?.type === "addons-event"
              ? item?.image?.url
              : require("../../../../assets/images/ticket.png"),
          isTooltip: false,
        },
        {
          _id: "quantity",
          type: "text",
          align: "center",
          cellClass: "!min-w-[100px]",
          data: item?.quantity === -1 ? "Unlimited" : item?.quantity || "0",
        },
        {
          _id: "booking/fullpaid",
          type: "text",
          align: "center",
          cellClass: "!min-w-[100px]",
          data:
            item?.paymentType !== "free" ? (
              <Badge
                type={item?.fullPaid !== "no" ? "danger" : "warning"}
                className={"rounded-full py-1 text-xs font-medium"}
                label={item?.fullPaid === "no" ? "booked" : "full paid"}
              />
            ) : (
              "-"
            ),
        },
        {
          _id: "price",
          type: "text",
          align: "center",
          cellClass: "!min-w-[100px]",
          data:
            item?.paymentType !== "free"
              ? `${event?.data?.currency?.symbol || "$"} ${parseFloat(
                  item?.price
                ).toFixed(2)}`
              : "Free",
        },
        {
          _id: "salesStartDate",
          type: "text",
          align: "center",
          cellClass: "!min-w-[100px] [&>div]:whitespace-nowrap",
          data: item?.salesStartDate
            ? `${dayjs(item?.salesStartDate).format("MMM DD, YYYY")} at ${dayjs(
                item?.salesStartDate
              ).format("hh:mm A")}`
            : "-",
        },
        {
          _id: "salesEndDate",
          type: "text",
          align: "center",
          cellClass: "!min-w-[100px] [&>div]:whitespace-nowrap",
          data: item?.salesEndDate
            ? `${dayjs(item?.salesEndDate).format("MMM DD, YYYY")} at ${dayjs(
                item?.salesEndDate
              ).format("hh:mm A")}`
            : "-",
        },
        {
          _id: "type",
          type: "text",
          align: "center",
          cellClass: "!min-w-[120px]",
          data: (
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border transition-all duration-200 select-none hover:brightness-95 hover:scale-105`}
              style={{
                backgroundColor: typeStyle.backgroundColor,
                color: typeStyle.color,
                borderColor: typeStyle.borderColor,
              }}
              title="Click to cycle through type options"
            >
              {formatTypeText(item?.type)}
            </span>
          ),
        },
        {
          _id: "action",
          type: "action",
          align: "right",
          actionData: [
            {
              _id: "Edit",
              name: t("Edit"),
              icon: "fa-regular fa-pen-to-square",
              standout: false,
              onClick: () => {
                navigate(
                  `/${lang}/user/events/manage-${
                    item?.type === "addons-event" ? "addons" : "ticket"
                  }/${item?.eventData?.uid}/${item?.uid}`
                );
              },
            },
            {
              _id: "Delete",
              name: t("Delete"),
              icon: "fa-regular fa-trash-can",
              standout: true,
              onClick: () => {
                setDeleteModal({
                  isOpen: true,
                  data: item,
                });
              },
            },
          ],
        },
      ],
    };
  });

  const handleOrdering = (orderedList, originalList) => {
    console.log("New order:", orderedList);
  };

  //   ticket delete handler =================>
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await ticketDelete({
        ids: Array.isArray(payload) ? payload : [payload],
      });
      return {
        res,
        payload: Array.isArray(payload) ? payload : [payload],
      };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        queryClient.setQueryData(
          ["ticket", filters, search, eventId],
          (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = stateData?.filter(
                (item) => !payload?.includes(item?._id)
              );

              return { ...state, data: stateData };
            }
          }
        );

        if (ticket?.metadata?.totalDocs > filters?.limit) {
          queryClient.invalidateQueries(["ticket", filters, search, eventId]);
        }
        toast.success("Ticket deleted successfully!");
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      toast.error("Failed to delete ticket");
      console.log(error);
    },
  });

  return (
    <>
      <div className="w-full flex flex-col pb-10 bg-white rounded-md">
        <div className="space-y-8 py-8 px-5">
          <div className="flex flex-wrap xl:flex-nowrap xl:gap-0 gap-3 items-center justify-between">
            <h2 className="text-xl xl:text-2xl text-slate-900 font-bold font-Montserrat uppercase">
              {t("Tickets overview")}
            </h2>
            <div className="flex flex-wrap md:flex-nowrap items-center justify-center gap-3">
              <div className="relative w-full md:w-auto">
                <Search
                  placeholder={t("Search here")}
                  divClasses={"border-slate-300 bg-slate-50"}
                  search={setKeyword}
                  searchValue={keyword}
                />
              </div>
              <div className="relative">
                <Button
                  buttonLabel={t("New Ticket")}
                  buttonFunction={() => {
                    navigate(`/${lang}/user/events/manage-ticket/${eventId}`);
                  }}
                />
              </div>
              <div className="relative">
                <Button
                  buttonLabel={t("New Addons")}
                  buttonFunction={() => {
                    navigate(`/${lang}/user/events/manage-addons/${eventId}`);
                  }}
                />
              </div>
            </div>
          </div>
          <Table
            tableHeadData={tableHeadData}
            tableData={tableData || []}
            originalData={tableData || []}
            handleOrdering={handleOrdering}
            tableActionDropdownContainer={"!w-48"}
            isLoder={ticketLoading}
            isFetching={ticketFetching}
            pageCount={ticket?.metadata?.totalPages || 1}
            currentPage={filters?.page}
            onPageChange={(val) => {
              setFilters((prev) => ({ ...prev, page: val }));
            }}
            selectedItems={[]}
            setSelectedItems={() => {}}
          />
        </div>
      </div>
      <DeleteModal
        open={deleteModal?.isOpen}
        loading={deletePending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        deleteHandler={() => {
          deleteHandler(deleteModal?.data?._id);
        }}
      />
    </>
  );
};

export default TicketsList;
